import { useNavigate } from "react-router";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardLayout, useDashboardTenantUsers } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { DashboardGrid } from "./dashboard-grid";
import { DashboardHeader, PERFORMANCE_OPTION } from "./dashboard-header";
import { T } from "./dashboard-page.i18n";

type DashboardPageProps = {
  isAdmin: boolean;
  currentUserId: string;
  target: DashboardTarget;
};

export function DashboardPage({ isAdmin, currentUserId, target }: DashboardPageProps) {
  const navigate = useNavigate();
  const t = useT(T);
  const { layout, isLoading, error } = useDashboardLayout(target);
  const { tenantUsers } = useDashboardTenantUsers();

  const userHref = (userId: string) =>
    userId === currentUserId ? "/" : `/?user=${encodeURIComponent(userId)}`;
  const openUser = (userId: string) => navigate(userHref(userId));
  const onSelect = (value: string) =>
    value === PERFORMANCE_OPTION ? navigate("/?dashboard=performance") : openUser(value);

  const selected =
    target.dashboard === "performance" ? PERFORMANCE_OPTION : (target.forUserId ?? currentUserId);

  return (
    <div className="space-y-6">
      <DashboardHeader
        layout={layout}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        selected={selected}
        tenantUsers={tenantUsers}
        onSelect={onSelect}
      />
      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      ) : error || !layout ? (
        <EmptyState>{t.unableToLoad}</EmptyState>
      ) : layout.widgets.length === 0 ? (
        <EmptyState>{t.noWidgets}</EmptyState>
      ) : (
        <DashboardGrid widgets={layout.widgets} target={target} userHref={userHref} />
      )}
    </div>
  );
}
